// ═══════════════════════════════════════════════════════════════
//  notificationsUtils.js — Helpers pour les notifications HomePage
// ═══════════════════════════════════════════════════════════════

// ─── Date utils ──────────────────────────────────────────
// `now` est injectable sur tous les helpers de date : sans ça, un test qui
// vérifie « mois déjà validé » ou « avant le 20 » dépend du jour où tourne la
// CI. Le défaut `new Date()` garde les appelants existants inchangés.
export function currentMonthKey(now = new Date()) {
  // Retourne "YYYY-MM" pour le mois courant
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function currentMonthLabel(now = new Date()) {
  // Retourne "avril 2026" pour le mois courant
  const d = new Date(now);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

export function monthLabel(monthKey) {
  // "2026-04" → "avril 2026"
  if (!monthKey) return "—";
  const [year, month] = monthKey.split("-");
  const d = new Date(parseInt(year), parseInt(month) - 1, 1);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

export function dayOfMonth(now = new Date()) {
  return new Date(now).getDate();
}

// Le rappel d'avancement est actif à partir du 20 du mois
export const RAPPEL_AVANCEMENT_JOUR = 20;

export function isRappelAvancementActif(now = new Date()) {
  return new Date(now).getDate() >= RAPPEL_AVANCEMENT_JOUR;
}

// ─── Notifications Parc machines ─────────────────────────
// Détenteur courant d'une sortie : dernier transfert s'il y en a, sinon
// l'emprunteur d'origine.
//
// Le champ destinataire d'un transfert est `toUserId` (ParcOutilTransfert.jsx,
// vérifié sur les documents réels de `outillageSorties`). Les deux valeurs
// portent un id de document `utilisateurs` (ex. "Bilardo"), jamais un uid
// Firebase.
//
// En principe les deux branches donnent la même réponse : le transfert
// réécrit `emprunteurId = toUserId` dans le MÊME updateDoc que l'arrayUnion
// sur `transferts`. Lire d'abord le dernier transfert n'est donc qu'un filet
// pour une sortie dont l'historique et l'emprunteur courant auraient divergé.
export function detenteurActuel(sortie) {
  const t = Array.isArray(sortie?.transferts) ? sortie.transferts : [];
  if (t.length) {
    const vers = t[t.length - 1]?.toUserId;
    if (vers) return vers;
  }
  return sortie?.emprunteurId || "";
}

// Nominatif, SANS exception de rôle. Un compteur qui affiche le parc entier
// n'apprend rien à personne : celui qui veut la vue globale ouvre le module.
// Même correction qu'en réserves plus bas — la portée du droit `view` n'est
// pas le périmètre d'une alerte.
export function computeParcNotifications({ outillageSorties, user, now = new Date() }) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const myId = user?._id || user?.id || "";
  if (!myId) return { count: 0, label: null };

  const enRetard = (outillageSorties || []).filter(s => {
    if (!s || s.dateRetourReelle || !s.dateRetourPrevue) return false;
    const d = new Date(s.dateRetourPrevue + "T00:00:00");
    if (Number.isNaN(d.getTime()) || d >= today) return false;
    return detenteurActuel(s) === myId;
  });

  return {
    count: enRetard.length,
    // « à rendre » et non « en retard » : le compteur ne parle plus du parc,
    // il parle de ce que CET utilisateur doit ramener.
    label: enRetard.length
      ? `${enRetard.length} outil${enRetard.length > 1 ? "s" : ""} à rendre`
      : null,
  };
}

// ─── Notifications Avancement ────────────────────────────
// Retourne le nombre de chantiers qui n'ont pas encore validé l'avancement du
// mois courant. Avant le 20 du mois, pas de notification.
//
// Nominatif, SANS exception de rôle : seuls comptent les chantiers auxquels
// CET utilisateur est affecté. L'ancien bloc `isPilotage` renvoyait tous les
// chantiers actifs à un Admin / Direction / Assistante, qui recevait donc en
// alerte le travail des autres — la vue d'ensemble est dans le module, pas
// dans un badge d'accueil.
//
// Lecture SEULE de `chantiers` : cette fonction n'écrit jamais rien.
export function computeAvancementNotifications({
  avancementValidations,
  chantiers,
  user,
  now = new Date(),
}) {
  const VIDE = { count: 0, label: null, chantiersNonValides: [] };
  if (!isRappelAvancementActif(now)) return VIDE;

  // Sans identité, aucun chantier ne peut être « le sien ».
  const uid = user?._id || user?.id;
  if (!uid) return VIDE;

  const monthKey = currentMonthKey(now);

  // Chantiers actifs auxquels l'utilisateur est affecté.
  const mesChantiers = (chantiers || []).filter(c => {
    if (!c) return false;
    // `archive` n'existe sur aucun document en base ; on le teste quand même
    // pour ne pas casser une éventuelle fiche historique qui le porterait.
    if (c.archive === true) return false;
    // Le champ réel est `statut` ("Actif"). Un chantier sans `statut` est
    // considéré actif : ne pas faire disparaître une fiche mal renseignée.
    if (c.statut && c.statut !== "Actif") return false;

    // Les affectations sont à la RACINE du document (vérifié sur
    // chantiers/002183) : `c.affectations` n'existe pas, l'ancienne lecture
    // ne trouvait donc JAMAIS personne et le nominatif était mort.
    // NB: on compare avec user._id ET user.id pour couvrir les deux formats
    //     utilisés dans Firestore (_id ajouté par DataContext, id = ancien schéma)
    return (
      c.conducteurId === uid ||
      (Array.isArray(c.chefChantierIds) && c.chefChantierIds.includes(uid)) ||
      (Array.isArray(c.monteurIds) && c.monteurIds.includes(uid))
    );
  });

  // Chantiers sans validation pour le mois courant
  const nonValides = mesChantiers.filter(c => {
    const chantierNum = c.num || c._id;
    const validation = (avancementValidations || []).find(
      v => v && v.chantierNum === chantierNum && v.mois === monthKey
    );
    return !validation;
  });

  return {
    count: nonValides.length,
    label: nonValides.length > 0
      ? `${nonValides.length} chantier${nonValides.length > 1 ? "s" : ""} à valider pour ${currentMonthLabel(now)}`
      : null,
    chantiersNonValides: nonValides,
  };
}

// ─── Helper pour vérifier si un chantier a été validé pour un mois donné ───
export function isAvancementValide(chantierNum, monthKey, avancementValidations) {
  if (!chantierNum || !monthKey) return false;
  return avancementValidations.some(
    v => v.chantierNum === chantierNum && v.mois === monthKey
  );
}

// ─── Récupère la validation d'un chantier/mois ───
export function getValidation(chantierNum, monthKey, avancementValidations) {
  return avancementValidations.find(
    v => v.chantierNum === chantierNum && v.mois === monthKey
  ) || null;
}

// ─── Notifications Réserves ──────────────────────────────
// Notifications réserves : TOUJOURS le périmètre personnel, jamais la
// portée du droit `view`. Un Admin/Direction a view:"all" pour consulter
// le module, ce qui ne veut pas dire qu'il doit recevoir en alerte les
// retards de toute l'entreprise (même correction qu'en v10.E côté
// commandes). La vue d'ensemble reste dans le module et les dashboards.
// Statuts qui ferment une réserve : elle sort de toute file de travail.
export const RESERVE_STATUTS_TERMINAUX = ["levee", "quitus_signe", "cloturee"];

// 🔴 DÉFINITION UNIQUE DE « EN RETARD ». Extraite de
// computeReservesNotifications pour être réutilisable telle quelle par le
// récapitulatif quotidien (functions/lib/notifications.js), qui couvre
// TOUTES les réserves et non les miennes. Une seconde définition dériverait
// de celle-ci en quelques semaines.
//  Deux cas, historiques et volontairement distincts :
//   • attribuée depuis 2 jours pleins SANS rendez-vous pris ;
//   • date limite dépassée.
export function estReserveEnRetard(r, now = new Date()) {
  if (!r) return false;
  if (r.statut === "attribuee" && !r.rdvPris && r.dateAffectation) {
    const jours = Math.round((now - new Date(r.dateAffectation)) / 86400000);
    if (jours >= 2) return true;
  }
  if (r.dateLimite && new Date(r.dateLimite) < now) return true;
  return false;
}

// 🔴 DÉFINITION UNIQUE DE « RDV IMMINENT » (mêmes raisons).
// Le champ en base est `rdvDate` (+ `rdvHeure`). L'ancien code lisait
// `dateRdv`, qui n'existe pas : la bannière 48 h ne s'est jamais déclenchée.
// rdvDate est un jour sans heure : on compare depuis MINUIT, sinon le RDV
// du jour même est déjà « passé » et disparaît de la bannière.
export function estRdvImminent(r, now = new Date()) {
  if (!r || !r.rdvPris || !r.rdvDate) return false;
  const debutJour = new Date(now); debutJour.setHours(0, 0, 0, 0);
  const dans48h = new Date(now.getTime() + 48 * 3600 * 1000);
  const d = new Date(r.rdvDate);
  return !Number.isNaN(d.getTime()) && d >= debutJour && d <= dans48h;
}

export function computeReservesNotifications({ reserves, user, now = new Date() }) {
  const myId = user?._id || user?.id || "";
  const miennes = (reserves || []).filter(r =>
    r && !RESERVE_STATUTS_TERMINAUX.includes(r.statut) && r.affecteAUserId === myId
  );
  if (!myId) return { retards: [], aTraiter: [], rdvImminents: [] };

  const retards = miennes.filter(r => estReserveEnRetard(r, now));
  const retardIds = new Set(retards.map(r => r._id || r.id));
  const aTraiter = miennes.filter(r => !retardIds.has(r._id || r.id));
  const rdvImminents = miennes.filter(r => estRdvImminent(r, now));

  return { retards, aTraiter, rdvImminents };
}

// ─── Notifications Tâches (lot T4) ───────────────────────────
// Fonction PURE : aucune lecture Firestore. Les deux listes lui sont passées
// par HomePage, qui les obtient en `getDocs` PONCTUEL (PERF-1 : pas un
// listener de plus).
//
// PÉRIMÈTRE NOMINATIF, comme partout ailleurs dans ce fichier :
//  • `tachesAssignees` = ce qui M'EST nommément assigné (where assigneUid ==
//    mon uid). Jamais « toutes les tâches du module » : un scope view "all"
//    autorise à CONSULTER, il ne justifie pas un compteur global.
//  • `tachesAValider` = les tâches de MES chantiers qu'un exécutant vient de
//    cocher et qui attendent MA validation — une dette personnelle, pas la
//    vue d'ensemble (même logique que la file de congés N1/N2).
//
// ⚠ « Encore à faire » se dit `statut === "a_faire"`, JAMAIS `statut !== "fait"`.
// La règle de sens unique héritée de la PWA veut que tout statut différent de
// "a_faire" (dont "en_cours") soit traité comme TERMINÉ — c'est ce que fait
// `estAFaire` dans cockpit/blocks/taches/useTaches.js, et c'est ce que voit
// l'utilisateur à l'écran. Compter autrement afficherait un badge « 3 » sur
// une liste qui n'en montre aucune. Le prédicat est recopié plutôt qu'importé :
// ce fichier n'importe RIEN (il est chargé par le test en node pur).
const TACHE_A_FAIRE = "a_faire";

export function computeTachesNotifications({ tachesAssignees, tachesAValider, user }) {
  const VIDE = { count: 0, label: null, aValider: null };
  if (!user) return VIDE;

  const enCours = (tachesAssignees || []).filter((t) => {
    if (!t) return false;
    if ((t.statut || "") !== TACHE_A_FAIRE) return false;
    // Déjà validée par le pilote : la tâche est close même si `statut` n'a
    // pas suivi (workflow 2 étapes — l'exécutant n'écrit pas `statut`).
    // L'état posé par validerTache est "VALIDE" (useTachesModule).
    if (t.validationConducteur?.etat === "VALIDE") return false;
    // Workflow 2 étapes : `basculerFait` n'écrit PAS `statut`, qui reste
    // "a_faire" jusqu'à la validation du pilote. Sans cette ligne, la personne
    // qui vient de cocher « fait » garde son badge « 1 tâche vous est
    // attribuée » tant que le pilote n'a pas validé — on la relance pour un
    // travail qu'elle a terminé. Elle a fait sa part ; la tâche est désormais
    // dans le compteur « à valider » de celui qui l'a confiée.
    if (t.validationMonteur?.etat === "FAIT") return false;
    return true;
  });

  const aValider = (tachesAValider || []).filter(Boolean);

  return {
    count: enCours.length,
    label: enCours.length
      ? `${enCours.length} tâche${enCours.length > 1 ? "s" : ""} vous ${enCours.length > 1 ? "sont" : "est"} attribuée${enCours.length > 1 ? "s" : ""}`
      : null,
    // T16 — EXACTEMENT une tâche : ses coordonnées, pour que la bannière la
    // nomme et que le clic y mène. À partir de deux, null : on ne nomme pas une
    // liste. Le NOM du chantier n'est pas calculé ici (pas de référentiel).
    aValider: aValider.length
      ? {
        count: aValider.length,
        label: `${aValider.length} tâche${aValider.length > 1 ? "s" : ""} à valider`,
        tache: aValider.length === 1
          ? {
            id: aValider[0].id || null,
            titre: String(aValider[0].titre || "").trim() || "(sans titre)",
            chantierId: aValider[0].chantierId != null ? String(aValider[0].chantierId) : null,
          }
          : null,
      }
      : null,
  };
}
